const COLOR_MAP = {
  blue:   { bg: 'bg-blue-50',   border: 'border-blue-100',   text: 'text-blue-700',   dot: 'bg-blue-500' },
  purple: { bg: 'bg-purple-50', border: 'border-purple-100', text: 'text-purple-700', dot: 'bg-purple-500' },
  amber:  { bg: 'bg-amber-50',  border: 'border-amber-100',  text: 'text-amber-700',  dot: 'bg-amber-500' },
  gray:   { bg: 'bg-gray-50',   border: 'border-gray-100',   text: 'text-gray-600',   dot: 'bg-gray-400' },
  indigo: { bg: 'bg-indigo-50', border: 'border-indigo-100', text: 'text-indigo-700', dot: 'bg-indigo-500' },
};

// Generic "why can't I delete this" popup — shows a colored icon header, a
// colored row per blocker (inventory, issuances, requirements, etc.), and an
// optional resolution action (e.g. "delete the open ones & retry", or a
// non-destructive alternative like "disable instead").
export default function DeleteBlockedModal({
  title,
  itemName,
  safe,
  blockers = [],           // [{ label, count, color }] — pre-filtered to count > 0
  resolutionAvailable = false,
  resolutionLabel,
  resolutionHint,
  blockedHint = 'Resolve this data elsewhere first, then try deleting again.',
  onResolve,
  onConfirmDelete,
  onClose,
  error,
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 p-6 w-full max-w-md">
        <div className="flex items-start gap-3 mb-4">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${safe ? 'bg-red-50' : 'bg-amber-50'}`}>
            <svg viewBox="0 0 20 20" fill="currentColor" className={`w-5 h-5 ${safe ? 'text-red-500' : 'text-amber-500'}`}>
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-slate-900">{title}</h3>
            <p className="text-sm text-slate-500 mt-0.5 truncate">"{itemName}"</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>

        {safe ? (
          <>
            <p className="text-slate-700 mb-4">Delete this? This cannot be undone.</p>
            {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
            <div className="flex gap-3 justify-end pt-3 border-t border-gray-100">
              <button className="btn-secondary" onClick={onClose}>Cancel</button>
              <button className="btn-danger" onClick={onConfirmDelete}>Delete</button>
            </div>
          </>
        ) : (
          <>
            <div className="space-y-2 mb-4">
              {blockers.map((b, i) => {
                const c = COLOR_MAP[b.color] || COLOR_MAP.gray;
                return (
                  <div key={i} className={`flex items-center justify-between px-3 py-2 rounded-lg border ${c.bg} ${c.border}`}>
                    <span className={`flex items-center gap-2 text-sm ${c.text}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
                      {b.label}
                    </span>
                    <span className={`text-sm font-semibold ${c.text}`}>{b.count}</span>
                  </div>
                );
              })}
            </div>

            {resolutionAvailable ? (
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">{resolutionHint}</p>
            ) : (
              <p className="text-sm text-gray-500 mb-4">{blockedHint}</p>
            )}

            {error && <p className="text-red-600 text-sm mb-3">{error}</p>}

            <div className="flex gap-3 justify-end pt-3 border-t border-gray-100">
              <button className="btn-secondary" onClick={onClose}>Close</button>
              {resolutionAvailable && (
                <button className="btn-danger" onClick={onResolve}>{resolutionLabel}</button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
